import { useState } from "react";
import { WhatsAppPhone } from "@/components/internal/whatsapp/preview/WhatsAppPhone";

/**
 * Step 4's phone. It shows how the agent opens a conversation; "Conversar com
 * o agente" turns it into a live test: the phone's own message field takes
 * what the firm writes as a client, and the agent answers with the answers
 * (or roteiro) given so far. Nothing is saved; "Recomeçar" clears it.
 */

const MAX_CHARS = 1000;
const MAX_MESSAGES = 20;

const pill =
  "inline-flex min-h-[44px] items-center rounded-full border border-black/[.08] bg-white px-5 text-sm font-semibold text-foreground shadow-[0_1px_2px_rgba(16,24,40,0.06)] disabled:opacity-50 dark:border-white/15 dark:bg-white/[.06]";

export default function PhonePreview({ firmName, example = [], canTry, tryHint = null, typingLabel = "digitando...", onReply }) {
  const [chatting, setChatting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);

  const full = messages.length >= MAX_MESSAGES - 1;

  const send = async () => {
    const text = draft.trim();
    if (!text || waiting || full) return;
    const next = [...messages, { from: "contact", text }];
    setMessages(next);
    setDraft("");
    setError(null);
    setWaiting(true);
    try {
      const reply = await onReply(next);
      setMessages([...next, { from: "agent", text: reply }]);
    } catch (err) {
      // The unanswered message goes back to the field, to send again.
      setMessages(messages);
      setDraft(text);
      setError(err?.message || "O agente não conseguiu responder agora. Tente de novo.");
    } finally {
      setWaiting(false);
    }
  };

  const restart = () => {
    setMessages([]);
    setDraft("");
    setError(null);
  };

  const conversation = chatting ? (waiting ? [...messages, { from: "agent", text: typingLabel }] : messages) : example;

  return (
    <aside aria-label="Prévia do atendimento" className="mx-auto flex flex-[0_0_300px] flex-col items-center gap-3.5">
      <WhatsAppPhone
        businessName={firmName || "Seu escritório"}
        conversation={conversation}
        composer={
          chatting
            ? {
                value: draft,
                onChange: (text) => setDraft(text.slice(0, MAX_CHARS)),
                onSend: send,
                disabled: waiting || full,
                placeholder: full ? "Recomece para continuar" : "Escreva como um cliente",
              }
            : null
        }
      />

      {error && (
        <p role="alert" className="w-full rounded-xl bg-rose-50 px-3 py-2 text-center text-[13px] text-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </p>
      )}

      {chatting ? (
        <div className="flex items-center gap-3">
          <button type="button" onClick={restart} disabled={waiting} className={pill}>
            Recomeçar
          </button>
          <button
            type="button"
            onClick={() => {
              restart();
              setChatting(false);
            }}
            disabled={waiting}
            className="min-h-[44px] text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Ver exemplo
          </button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setChatting(true)} disabled={!canTry} className={pill}>
            Conversar com o agente
          </button>
          {!canTry && tryHint && <p className="max-w-[260px] text-center text-[13px] text-muted-foreground">{tryHint}</p>}
        </>
      )}
    </aside>
  );
}
